import { z } from "zod";
import { readFileSync } from "node:fs";
import os from "node:os";
import path from "node:path";

/**
 * Canonical MCP server definition as stored in the vault
 * (`~/.appostle/mcp-servers/<name>.json`). Claude-shaped: `type` may be omitted
 * for stdio servers — a `command` implies stdio. Secrets are externalized as
 * `{env:VAR}` placeholders so plaintext never rides the synced vault.
 */
export const canonicalMcpServerSchema = z
  .object({
    type: z.enum(["stdio", "http", "sse"]).optional(),
    command: z.string().optional(),
    args: z.array(z.string()).optional(),
    env: z.record(z.string()).optional(),
    url: z.string().optional(),
    headers: z.record(z.string()).optional(),
  })
  .refine((value) => value.command != null || value.url != null, {
    message: "MCP server must define either `command` (stdio) or `url` (remote)",
  });

export type CanonicalMcpServer = z.infer<typeof canonicalMcpServerSchema>;

type CanonicalTransport = "stdio" | "http" | "sse";

function resolveTransport(server: CanonicalMcpServer): CanonicalTransport {
  if (server.type) {
    return server.type;
  }
  return server.command != null ? "stdio" : "http";
}

const ENV_PLACEHOLDER = /^\{env:([A-Za-z0-9_]+)\}$/;
const JSON_PLACEHOLDER = /^\{json:([^:]+):([A-Za-z0-9_.-]+)\}$/;

function expandHome(input: string): string {
  if (input === "~") {
    return os.homedir();
  }
  if (input.startsWith("~/")) {
    return path.join(os.homedir(), input.slice(2));
  }
  return input;
}

function readJsonPath(filePath: string, keyPath: string): string | null {
  let value: unknown;
  try {
    value = JSON.parse(readFileSync(expandHome(filePath), "utf8"));
  } catch {
    return null;
  }
  for (const part of keyPath.split(".")) {
    if (typeof value !== "object" || value === null || Array.isArray(value)) {
      return null;
    }
    value = (value as Record<string, unknown>)[part];
  }
  return typeof value === "string" ? value : null;
}

/**
 * Substitute a `{env:VAR}` placeholder with its value from `env`. If the
 * variable is unset, try a `{json:path:key.path}` placeholder against a local
 * JSON file. If neither resolves, the literal placeholder is left in place
 * rather than throwing or dropping the entry — the product intent is "land every
 * catalog server even if it can't connect yet". An unresolved placeholder
 * surfaces as a failed server in the provider UI, which is the cue for the user
 * to wire up the credential / path later. Non-placeholder strings pass through
 * untouched.
 */
function resolveEnvValue(value: string, env: Record<string, string | undefined>): string {
  const envMatch = ENV_PLACEHOLDER.exec(value);
  if (envMatch) {
    return env[envMatch[1]] ?? value;
  }
  const jsonMatch = JSON_PLACEHOLDER.exec(value);
  if (jsonMatch) {
    return readJsonPath(jsonMatch[1], jsonMatch[2]) ?? value;
  }
  return value;
}

function resolveEnvRecord(
  record: Record<string, string>,
  env: Record<string, string | undefined>,
): Record<string, string> {
  const resolved: Record<string, string> = {};
  for (const [key, value] of Object.entries(record)) {
    resolved[key] = resolveEnvValue(value, env);
  }
  return resolved;
}

/**
 * Claude's `mcpServers` entry shape. Claude does not resolve `{env:VAR}`
 * placeholders, so they are substituted to literals at compile time.
 */
export type ClaudeMcpEntry =
  | { type?: "stdio"; command: string; args?: string[]; env?: Record<string, string> }
  | { type: "http" | "sse"; url: string; headers?: Record<string, string> };

export function toClaudeMcpEntry(
  name: string,
  server: CanonicalMcpServer,
  env: Record<string, string | undefined>,
): ClaudeMcpEntry {
  if (resolveTransport(server) === "stdio") {
    if (server.command == null) {
      throw new Error(`MCP server '${name}' is stdio but has no command`);
    }
    return {
      ...(server.type ? { type: "stdio" as const } : {}),
      command: server.command,
      ...(server.args && server.args.length > 0
        ? { args: server.args.map((arg) => resolveEnvValue(arg, env)) }
        : {}),
      ...(server.env && Object.keys(server.env).length > 0
        ? { env: resolveEnvRecord(server.env, env) }
        : {}),
    };
  }
  if (server.url == null) {
    throw new Error(`MCP server '${name}' is remote but has no url`);
  }
  return {
    type: server.type === "sse" ? "sse" : "http",
    url: server.url,
    ...(server.headers ? { headers: resolveEnvRecord(server.headers, env) } : {}),
  };
}

/**
 * OpenCode's `mcp` entry shape. OpenCode resolves `{env:VAR}` natively at
 * runtime, so placeholders are passed through untouched.
 */
export type OpenCodeMcpEntry =
  | { type: "local"; command: string[]; environment?: Record<string, string>; enabled: boolean }
  | { type: "remote"; url: string; headers?: Record<string, string>; enabled: boolean };

export function toOpenCodeMcpEntry(name: string, server: CanonicalMcpServer): OpenCodeMcpEntry {
  if (resolveTransport(server) === "stdio") {
    if (server.command == null) {
      throw new Error(`MCP server '${name}' is stdio but has no command`);
    }
    return {
      type: "local",
      command: [server.command, ...(server.args ?? [])],
      ...(server.env && Object.keys(server.env).length > 0 ? { environment: server.env } : {}),
      enabled: false,
    };
  }
  if (server.url == null) {
    throw new Error(`MCP server '${name}' is remote but has no url`);
  }
  return {
    type: "remote",
    url: server.url,
    ...(server.headers ? { headers: server.headers } : {}),
    enabled: false,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export interface MergeInput<TEntry> {
  /** The provider's existing parsed config object. Untouched except for the MCP key. */
  config: Record<string, unknown>;
  /** Appostle-owned entries to write, keyed by server name. */
  entries: Record<string, TEntry>;
  /** Server names Appostle wrote on a previous run, so retired ones can be pruned. */
  previouslyOwned: string[];
}

export interface MergeResult {
  config: Record<string, unknown>;
  /** Server names Appostle now owns — persist this for the next prune. */
  owned: string[];
}

/**
 * Surgically merge Appostle-owned MCP servers into `config[key]`, preserving
 * every other key in the config and every server a user added by hand. Only
 * servers Appostle previously owned but no longer ships are removed.
 */
function mergeIntoConfig<TEntry>(
  config: Record<string, unknown>,
  key: string,
  entries: Record<string, TEntry>,
  previouslyOwned: string[],
): MergeResult {
  const merged: Record<string, unknown> = isRecord(config[key]) ? { ...config[key] } : {};
  const ownedNow = Object.keys(entries);
  for (const name of previouslyOwned) {
    if (!ownedNow.includes(name)) {
      delete merged[name];
    }
  }
  for (const [name, entry] of Object.entries(entries)) {
    merged[name] = entry;
  }
  return { config: { ...config, [key]: merged }, owned: ownedNow };
}

export function mergeIntoClaudeConfig(input: MergeInput<ClaudeMcpEntry>): MergeResult {
  return mergeIntoConfig(input.config, "mcpServers", input.entries, input.previouslyOwned);
}

export function mergeIntoOpenCodeConfig(input: MergeInput<OpenCodeMcpEntry>): MergeResult {
  return mergeIntoConfig(input.config, "mcp", input.entries, input.previouslyOwned);
}

/**
 * Codex's `mcp_servers` entry shape (TOML). Like Claude, Codex does not resolve
 * `{env:VAR}` placeholders, so they are substituted to literals at compile time.
 */
export type CodexMcpEntry =
  | { command: string; args?: string[]; env?: Record<string, string> }
  | { url: string; http_headers?: Record<string, string> };

export function toCodexMcpEntry(
  name: string,
  server: CanonicalMcpServer,
  env: Record<string, string | undefined>,
): CodexMcpEntry {
  if (resolveTransport(server) === "stdio") {
    if (server.command == null) {
      throw new Error(`MCP server '${name}' is stdio but has no command`);
    }
    return {
      command: server.command,
      ...(server.args && server.args.length > 0
        ? { args: server.args.map((arg) => resolveEnvValue(arg, env)) }
        : {}),
      ...(server.env && Object.keys(server.env).length > 0
        ? { env: resolveEnvRecord(server.env, env) }
        : {}),
    };
  }
  if (server.url == null) {
    throw new Error(`MCP server '${name}' is remote but has no url`);
  }
  return {
    url: server.url,
    ...(server.headers && Object.keys(server.headers).length > 0
      ? { http_headers: resolveEnvRecord(server.headers, env) }
      : {}),
  };
}

export function mergeIntoCodexConfig(input: MergeInput<CodexMcpEntry>): MergeResult {
  return mergeIntoConfig(input.config, "mcp_servers", input.entries, input.previouslyOwned);
}
